import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSCheckboxToggle,
  WSForm,
  WSList,
  WSSelect,
  WSSelectOption,
  WSText
} from "@wingspanhq/fe-component-library";
import orderBy from "lodash/orderBy";
import React from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesEngagements } from "@query";
import { WSQueries } from "../../../query/WSQuery";
import { getCreateRequirementDefinitionPathname } from "../../RequirementDefinitions/paths/helpers";
import {
  getRequirementDefinitionIsWayfinder,
  getRequirementDefinitionName
} from "../../RequirementDefinitions/utils";
import { AddRequirementInformation } from "../../Requirements/components/AddRequirementInformation/AddRequirementInformation";
import { useQueryExtendedRequirementDefinitions } from "../../../query/requirementDefinitions/query/useQueryExtendedRequirementDefinitions";
import { selectorTemplateParamsFromExtendedRequirementDefinition } from "../../Requirements/modules/InsuranceCoverage/selectors/selectorTemplateParamsFromExtendedRequirementDefinition";
import { ENGAGEMENT_REQUIREMENT_TYPES } from "../../RequirementDefinitions/types";

type AddRequirementToEngagementModalProps = {
  engagementId: string;
  requirementDefinitionIds?: string[];
  onClose: () => void;
};

type FormData = {
  requirementDefinitionId: string | undefined;
};

const validationSchema = Yup.object().shape({
  requirementDefinitionId: Yup.string().required("Required"),
  confirm: Yup.boolean().required("Required")
});

const AddRequirementToEngagementModal: React.FC<
  AddRequirementToEngagementModalProps
> = ({ requirementDefinitionIds = [], engagementId, onClose }) => {
  const history = useHistory();
  const { openSnackbar } = useWSSnackbar();

  const queryRequirementsDefinitions = useQueryExtendedRequirementDefinitions(
    "contextType",
    { filter: { type: { anyOf: ENGAGEMENT_REQUIREMENT_TYPES } } }
  );
  const [addRequirementToEngagement, addRequirementToEngagementMeta] =
    queriesEngagements.addEngagementRequirement.useMutation();

  return (
    <WSQueries queries={{ queryRequirementsDefinitions }}>
      {({ queryRequirementsDefinitionsData }) => {
        // v1 served this dropdown sorted by `updatedAt` desc; the v3 list has
        // no sort parameter, so the ordering is reapplied client-side.
        const sortedRequirementDefinitions = orderBy(
          queryRequirementsDefinitionsData,
          requirement => requirement.events?.updatedAt ?? 0,
          "desc"
        );

        return (
          <>
            <WSText.Paragraph weight="medium" mb="S">
              Engagement information
            </WSText.Paragraph>
            <WSForm<FormData>
              validationSchema={validationSchema}
              onSubmit={async values => {
                await addRequirementToEngagement(
                  {
                    engagementId,
                    engagementRequirementCreate: {
                      requirementDefinitionId: values.requirementDefinitionId!
                    }
                  },
                  {
                    onSuccess() {
                      openSnackbar({
                        message: "Requirement added to engagement",
                        type: "success"
                      });
                      onClose();
                    }
                  }
                );
              }}
            >
              <WSList gap="XL">
                <WSForm.Field
                  name="requirementDefinitionId"
                  label="Requirement"
                  component={WSSelect}
                  componentProps={{
                    required: true,
                    menuFooterAction: {
                      label: "Create new requirement",
                      icon: "plus",
                      onClick() {
                        history.push(getCreateRequirementDefinitionPathname(), {
                          engagementId
                        });
                      }
                    },
                    menuFooterText: "Don’t see a requirement?",
                    options: sortedRequirementDefinitions.map(
                      (requirement): WSSelectOption => {
                        const isWayfinder = getRequirementDefinitionIsWayfinder(
                          selectorTemplateParamsFromExtendedRequirementDefinition(
                            requirement
                          )
                        );

                        const helperText = getRequirementDefinitionName(
                          selectorTemplateParamsFromExtendedRequirementDefinition(
                            requirement
                          )
                        );

                        return {
                          value: requirement.id!,
                          avatar: isWayfinder
                            ? {
                                type: "icon",
                                icon: "compass",
                                format: "square",
                                size: "S",
                                colorBackground: "garnet"
                              }
                            : undefined,
                          label: requirement.name!,
                          labels: isWayfinder ? undefined : [helperText],
                          delisted: requirementDefinitionIds.includes(
                            requirement.id!
                          )
                        };
                      }
                    ),
                    emptyState: {
                      type: "documents",
                      title: "No requirements have been created",
                      description:
                        "Once a requirement is created, it will appear here",
                      actions: {
                        buttons: [
                          {
                            label: "Create new requirement",
                            kind: "Secondary",
                            onClick() {
                              history.push(
                                getCreateRequirementDefinitionPathname(),
                                {
                                  engagementId
                                }
                              );
                            }
                          }
                        ]
                      }
                    }
                  }}
                />
                <WSForm.Values names={["requirementDefinitionId"]}>
                  {({ requirementDefinitionId }) => {
                    const requirementDefinition =
                      queryRequirementsDefinitionsData.find(
                        r => r.id === requirementDefinitionId
                      );

                    return (
                      <AddRequirementInformation
                        engagementId={engagementId}
                        requirementDefinition={requirementDefinition}
                      />
                    );
                  }}
                </WSForm.Values>

                <WSForm.Field
                  name="confirm"
                  component={WSCheckboxToggle}
                  componentProps={{
                    label: `Confirm applying this requirement to existing and future contractors by proceeding`
                  }}
                />
                <ErrorAlert
                  context="Other"
                  error={addRequirementToEngagementMeta.error}
                />
                <WSForm.Values names={["requirementDefinitionId", "confirm"]}>
                  {({ requirementDefinitionId, confirm }) => {
                    const isValid = requirementDefinitionId && confirm;

                    return (
                      <WSActions
                        alignment="fill"
                        buttons={[
                          {
                            label: "Add requirement",
                            kind: "Primary",
                            name: "submit",
                            type: "submit",
                            disabled: !isValid
                          },
                          {
                            label: "Cancel",
                            kind: "Secondary",
                            type: "button",
                            onClick() {
                              onClose();
                            }
                          }
                        ]}
                      />
                    );
                  }}
                </WSForm.Values>
              </WSList>
            </WSForm>
          </>
        );
      }}
    </WSQueries>
  );
};

export function useAddRequirementToEngagementModal() {
  return useWSModal(AddRequirementToEngagementModal, {
    title: "Add requirement",
    size: "S"
  });
}
